import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    AuthenticatedDownloadError,
    downloadAuthenticatedFile,
} from '@/lib/downloadAuthenticated';
import { buildTrackerProgressExportPath } from '@/lib/trackerProgressExport';
import { useToast } from '@/hooks/use-toast';

interface TrackerProgressExcelExportButtonProps {
    cycleId: string | null;
    retailerId: string | null;
    taskDefinitionIds: string | null;
}

export function TrackerProgressExcelExportButton({
    cycleId,
    retailerId,
    taskDefinitionIds,
}: TrackerProgressExcelExportButtonProps) {
    const [isExporting, setIsExporting] = useState(false);
    const { toast } = useToast();

    const handleExport = async () => {
        if (!cycleId || !retailerId) {
            return;
        }

        setIsExporting(true);
        try {
            await downloadAuthenticatedFile(
                buildTrackerProgressExportPath(cycleId, retailerId, taskDefinitionIds),
                'cockpit-progress.xlsx',
            );
        } catch (error) {
            toast({
                title: 'Export failed',
                description: error instanceof AuthenticatedDownloadError
                    ? error.message
                    : 'Could not download the progress report.',
                variant: 'destructive',
            });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 gap-2"
            disabled={!cycleId || !retailerId || isExporting}
            onClick={() => {
                void handleExport();
            }}
        >
            <Download className="h-4 w-4" />
            {isExporting ? 'Exporting…' : 'Export Excel'}
        </Button>
    );
}
